// =============================================================================
// Investor Profile Page
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  UserCircleIcon,
  ShieldCheckIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  EyeIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Card, Button, CodeInput } from '../../components/ui';
import { AuthenticatorModal } from '../../components/ui/AuthenticatorModal';
import { useAuth } from '../../hooks/useAuth';
import { investorService, type ProfileWithMaskedData } from '../../services/investor.service';
import { authService } from '../../services/auth.service';

// =============================================================================
// Types for Editing
// =============================================================================

type EditType = 'basic' | 'taxId' | 'banking' | null;

interface BasicFormData {
  legalName: string;
  entityName: string;
  authorizedSigner: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

interface TaxIdFormData {
  ssn: string;
  fein: string;
}

interface BankingFormData {
  bankName: string;
  bankAccountType: 'CHECKING' | 'SAVINGS' | '';
  routingNumber: string;
  accountNumber: string;
}

// =============================================================================
// Types
// =============================================================================

type Tab = 'profile' | 'security';

// =============================================================================
// Press-to-Reveal Component
// =============================================================================

interface PressToRevealProps {
  profileId: string;
  field: 'ssn' | 'fein' | 'routing' | 'account';
  maskedValue: string;
  hasValidSession: boolean;
  onRequestReveal: () => void;
  formatValue?: (value: string) => string;
}

function PressToRevealButton({
  profileId,
  field,
  maskedValue,
  hasValidSession,
  onRequestReveal,
  formatValue,
}: PressToRevealProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  // Clear cached value when session becomes invalid
  useEffect(() => {
    if (!hasValidSession) {
      setRevealedValue(null);
    }
  }, [hasValidSession]);

  // Fetch data when user starts holding (if they have valid session)
  const handleMouseDown = async () => {
    if (!hasValidSession) {
      onRequestReveal();
      return;
    }

    // If we already have the value cached, just reveal it (no API call needed)
    if (revealedValue) {
      setIsRevealed(true);
      return;
    }

    setIsFetching(true);
    try {
      const dataTypeMap: Record<string, 'ssn' | 'fein' | 'bank_account' | 'routing_number'> = {
        ssn: 'ssn',
        fein: 'fein',
        routing: 'routing_number',
        account: 'bank_account',
      };
      const result = await investorService.revealSensitiveData(profileId, dataTypeMap[field]);
      setRevealedValue(result.value);
      setIsRevealed(true);
    } catch (error: unknown) {
      console.error('Failed to reveal data:', error);
      // Only show authenticator modal for auth errors (401/403)
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 401 || axiosError.response?.status === 403) {
        onRequestReveal();
      }
      // For other errors (rate limit, network, etc.), just log and don't show modal
    } finally {
      setIsFetching(false);
    }
  };

  const handleMouseUp = () => {
    setIsRevealed(false);
  };

  const handleMouseLeave = () => {
    setIsRevealed(false);
  };

  const handleTouchStart = async () => {
    await handleMouseDown();
  };

  const handleTouchEnd = () => {
    setIsRevealed(false);
  };

  const displayValue = isRevealed && revealedValue
    ? (formatValue ? formatValue(revealedValue) : revealedValue)
    : maskedValue;

  return (
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900 font-mono">
          {displayValue}
        </p>
      </div>
      <button
        type="button"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        disabled={isFetching}
        className={`ml-3 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors select-none ${
          isRevealed
            ? 'bg-amber-100 border-amber-300 text-amber-800'
            : hasValidSession
            ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            : 'bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100'
        } ${isFetching ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span className="flex items-center gap-1">
          {isFetching ? (
            <span className="animate-spin h-3.5 w-3.5 border-2 border-gray-300 border-t-primary-600 rounded-full" />
          ) : (
            <EyeIcon className="h-3.5 w-3.5" />
          )}
          {isRevealed ? 'Viewing...' : hasValidSession ? 'Hold to View' : 'Reveal'}
        </span>
      </button>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function ProfilePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as Tab) || 'profile';
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [isLoading, setIsLoading] = useState(true);
  const [profiles, setProfiles] = useState<ProfileWithMaskedData[]>([]);

  // TOTP (Authenticator) verification state - 5 minute session
  const [showAuthenticator, setShowAuthenticator] = useState(false);
  const [totpSessionExpiry, setTotpSessionExpiry] = useState<Date | null>(null);

  // Reset Authenticator modal state
  const [showResetAuthModal, setShowResetAuthModal] = useState(false);
  const [resetQrCode, setResetQrCode] = useState<string>('');
  const [resetSecret, setResetSecret] = useState<string>('');
  const [resetCode, setResetCode] = useState('');
  const [resetError, setResetError] = useState<string>('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetStep, setResetStep] = useState<'confirm' | 'setup' | 'verify'>('confirm');
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Profile editing state
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [editType, setEditType] = useState<EditType>(null);
  const [basicFormData, setBasicFormData] = useState<BasicFormData>({
    legalName: '', entityName: '', authorizedSigner: '', address: '', city: '', state: '', zipCode: ''
  });
  const [taxIdFormData, setTaxIdFormData] = useState<TaxIdFormData>({ ssn: '', fein: '' });
  const [bankingFormData, setBankingFormData] = useState<BankingFormData>({
    bankName: '', bankAccountType: '', routingNumber: '', accountNumber: ''
  });
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string>('');
  const [pendingSensitiveEdit, setPendingSensitiveEdit] = useState<{ profileId: string; type: 'taxId' | 'banking' } | null>(null);


  useEffect(() => {
    loadProfileData();
  }, []);

  // Update tab when URL changes
  useEffect(() => {
    const tabParam = searchParams.get('tab') as Tab;
    if (tabParam && ['profile', 'security'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Invalidate TOTP session when leaving the page or switching tabs (visibility change)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // User switched away from this tab - invalidate session
        setTotpSessionExpiry(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup: invalidate session when component unmounts (leaving page)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setTotpSessionExpiry(null);
    };
  }, []);

  const loadProfileData = async () => {
    setIsLoading(true);
    try {
      const profileData = await investorService.getProfiles();
      setProfiles(profileData);
    } catch (error) {
      console.error('Failed to load profile data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset Authenticator functions
  const handleOpenResetAuth = () => {
    setShowResetAuthModal(true);
    setResetStep('confirm');
    setResetQrCode('');
    setResetSecret('');
    setResetCode('');
    setResetError('');
    setShowManualEntry(false);
  };

  const handleCloseResetAuth = () => {
    setShowResetAuthModal(false);
    setResetStep('confirm');
    setResetQrCode('');
    setResetSecret('');
    setResetCode('');
    setResetError('');
    setShowManualEntry(false);
  };

  const handleConfirmReset = async () => {
    setResetLoading(true);
    setResetError('');
    try {
      const response = await authService.resetTotp();
      if (response.success && response.qrCodeDataUrl && response.secret) {
        setResetQrCode(response.qrCodeDataUrl);
        setResetSecret(response.secret);
        setResetStep('setup');
      } else {
        setResetError(response.error || 'Failed to reset authenticator');
      }
    } catch (err) {
      console.error('Reset TOTP error:', err);
      setResetError('Failed to reset authenticator. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleVerifyResetCode = async (code: string) => {
    if (code.length !== 6 || resetLoading) return;
    setResetLoading(true);
    setResetError('');
    try {
      const response = await authService.completeTotpSetup(code);
      if (response.success) {
        setTotpSessionExpiry(response.expiresAt ? new Date(response.expiresAt) : null);
        handleCloseResetAuth();
        alert('Authenticator has been reset successfully. You can now use your new authenticator app.');
      } else {
        setResetError(response.error || 'Invalid code. Please try again.');
        setResetCode('');
      }
    } catch (err) {
      console.error('Verify reset code error:', err);
      setResetError('Verification failed. Please try again.');
      setResetCode('');
    } finally {
      setResetLoading(false);
    }
  };

  const formatSecret = (s: string): string => {
    return s.match(/.{1,4}/g)?.join(' ') || s;
  };

  // Format SSN as XXX-XX-XXXX
  const formatSSN = (ssn: string): string => {
    const digits = ssn.replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    }
    return ssn; // Return as-is if not 9 digits
  };

  // Format FEIN as XX-XXXXXXX (9 digits total)
  const formatFEIN = (fein: string): string => {
    const digits = fein.replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return fein; // Return as-is if not 9 digits
  };

  // =========================================================================
  // Profile Editing Functions
  // =========================================================================

  // Start editing basic info (no TOTP required)
  const handleStartBasicEdit = (profile: ProfileWithMaskedData) => {
    setEditingProfileId(profile.id);
    setEditType('basic');
    setBasicFormData({
      legalName: profile.legalName || '',
      entityName: profile.entityName || '',
      authorizedSigner: profile.authorizedSigner || '',
      address: profile.address || '',
      city: profile.city || '',
      state: profile.state || '',
      zipCode: profile.zipCode || '',
    });
    setEditError('');
  };

  // Start editing sensitive info (requires TOTP verification)
  const handleStartSensitiveEdit = async (profile: ProfileWithMaskedData, type: 'taxId' | 'banking') => {
    // First check if TOTP is enabled
    try {
      const status = await authService.getTotpStatus();
      if (!status.enabled) {
        alert('Please set up your authenticator in the Security tab above before editing sensitive information.');
        return;
      }

      // Check if user has valid TOTP session
      if (hasValidTotpSession()) {
        // Already verified, open edit modal
        openSensitiveEditModal(profile, type);
      } else {
        // Need TOTP verification first
        setPendingSensitiveEdit({ profileId: profile.id, type });
        setShowAuthenticator(true);
      }
    } catch (error) {
      console.error('Error checking TOTP status:', error);
      alert('Failed to check authenticator status. Please try again.');
    }
  };

  // Open sensitive edit modal (called after TOTP verification)
  const openSensitiveEditModal = (profile: ProfileWithMaskedData, type: 'taxId' | 'banking') => {
    setEditingProfileId(profile.id);
    setEditType(type);
    setEditError('');

    if (type === 'taxId') {
      setTaxIdFormData({ ssn: '', fein: '' });
    } else {
      setBankingFormData({
        bankName: profile.bankName || '',
        bankAccountType: profile.bankAccountType || '',
        routingNumber: '',
        accountNumber: '',
      });
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingProfileId(null);
    setEditType(null);
    setEditError('');
    setPendingSensitiveEdit(null);
  };

  // Save basic info (no TOTP required)
  const handleSaveBasic = async () => {
    if (!editingProfileId) return;
    setEditSaving(true);
    setEditError('');

    try {
      const profile = profiles.find(p => p.id === editingProfileId);
      const dataToSave: Record<string, string> = {};

      // Only include fields that apply to the profile type
      if (profile?.type === 'INDIVIDUAL') {
        dataToSave.legalName = basicFormData.legalName;
      } else {
        dataToSave.entityName = basicFormData.entityName;
        dataToSave.authorizedSigner = basicFormData.authorizedSigner;
      }
      dataToSave.address = basicFormData.address;
      dataToSave.city = basicFormData.city;
      dataToSave.state = basicFormData.state;
      dataToSave.zipCode = basicFormData.zipCode;

      await investorService.updateProfile(editingProfileId, dataToSave);
      await loadProfileData(); // Refresh profiles
      handleCancelEdit();
    } catch (error) {
      console.error('Error saving profile:', error);
      setEditError('Failed to save changes. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  // Save sensitive info (TOTP already verified)
  const handleSaveSensitive = async () => {
    if (!editingProfileId) return;
    setEditSaving(true);
    setEditError('');

    try {
      const profile = profiles.find(p => p.id === editingProfileId);

      if (editType === 'taxId') {
        const dataToSave: { ssn?: string; fein?: string } = {};
        if (profile?.type === 'INDIVIDUAL' && taxIdFormData.ssn) {
          // Remove any formatting from SSN
          dataToSave.ssn = taxIdFormData.ssn.replace(/\D/g, '');
        } else if (profile?.type !== 'INDIVIDUAL' && taxIdFormData.fein) {
          // Remove any formatting from FEIN
          dataToSave.fein = taxIdFormData.fein.replace(/\D/g, '');
        }
        await investorService.updateProfileSensitive(editingProfileId, dataToSave);
      } else if (editType === 'banking') {
        const dataToSave: {
          bankName?: string;
          bankAccountType?: 'CHECKING' | 'SAVINGS';
          routingNumber?: string;
          accountNumber?: string;
        } = {};
        if (bankingFormData.bankName) dataToSave.bankName = bankingFormData.bankName;
        if (bankingFormData.bankAccountType) dataToSave.bankAccountType = bankingFormData.bankAccountType;
        if (bankingFormData.routingNumber) dataToSave.routingNumber = bankingFormData.routingNumber.replace(/\D/g, '');
        if (bankingFormData.accountNumber) dataToSave.accountNumber = bankingFormData.accountNumber.replace(/\D/g, '');
        await investorService.updateProfileSensitive(editingProfileId, dataToSave);
      }

      await loadProfileData(); // Refresh profiles
      handleCancelEdit();
    } catch (error: unknown) {
      console.error('Error saving sensitive data:', error);
      const axiosError = error as { response?: { data?: { error?: string } } };
      const errorMessage = axiosError.response?.data?.error || 'Failed to save changes. Please try again.';
      setEditError(errorMessage);
    } finally {
      setEditSaving(false);
    }
  };

  // Check if user has a valid TOTP session (verified within last 5 minutes)
  const hasValidTotpSession = useCallback((): boolean => {
    if (!totpSessionExpiry) return false;
    return new Date() < totpSessionExpiry;
  }, [totpSessionExpiry]);

  // Request reveal - if no valid session, show authenticator modal
  const handleRequestReveal = useCallback(() => {
    // Show authenticator modal to verify TOTP
    setShowAuthenticator(true);
  }, []);

  // Called when TOTP verification is successful
  const handleTotpVerified = useCallback((expiresAt: Date) => {
    setTotpSessionExpiry(expiresAt);
    setShowAuthenticator(false);

    // If there's a pending sensitive edit, open the edit modal
    if (pendingSensitiveEdit) {
      const profile = profiles.find(p => p.id === pendingSensitiveEdit.profileId);
      if (profile) {
        openSensitiveEditModal(profile, pendingSensitiveEdit.type);
      }
      setPendingSensitiveEdit(null);
    }
    // Otherwise, user can use press-and-hold to reveal data for 5 minutes
  }, [pendingSensitiveEdit, profiles]);


  const getProfileTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      LLC: 'LLC',
      TRUST: 'Trust',
      CORPORATION: 'Corporation',
      PARTNERSHIP: 'Partnership',
      IRA: 'IRA',
    };
    return labels[type] || type;
  };

  const tabs = [
    { id: 'profile' as Tab, name: 'Profile Info', icon: UserCircleIcon },
    { id: 'security' as Tab, name: 'Security', icon: ShieldCheckIcon },
  ];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded" />
        <div className="h-12 bg-gray-200 rounded-lg" />
        <div className="h-64 bg-gray-200 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings and investor profiles
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Authenticator Modal for TOTP verification */}
      <AuthenticatorModal
        isOpen={showAuthenticator}
        onClose={() => {
          setShowAuthenticator(false);
          setPendingSensitiveEdit(null); // Clear pending edit if cancelled
        }}
        onVerified={handleTotpVerified}
        title="Verification Required"
        description={pendingSensitiveEdit
          ? "Enter the code from your authenticator app to edit sensitive information."
          : "Enter the code from your authenticator app to access sensitive information."
        }
      />

      {/* Reset Authenticator Modal */}
      {showResetAuthModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCloseResetAuth} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl">
              {/* Header */}
              <div className="border-b border-gray-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {resetStep === 'confirm' ? 'Reset Authenticator' : 'Set Up New Authenticator'}
                  </h2>
                  <button
                    onClick={handleCloseResetAuth}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6">
                {/* Confirm Step */}
                {resetStep === 'confirm' && (
                  <div className="text-center">
                    <div className="mb-4 mx-auto rounded-full bg-amber-100 p-3 w-fit">
                      <ShieldCheckIcon className="h-8 w-8 text-amber-600" />
                    </div>
                    <p className="mb-4 text-sm text-gray-600">
                      Are you sure you want to reset your authenticator app? This will invalidate your current authenticator and generate a new QR code.
                    </p>
                    <p className="mb-6 text-sm text-gray-500">
                      You will need to scan the new QR code with Microsoft Authenticator or Google Authenticator.
                    </p>
                    {resetError && (
                      <p className="mb-4 text-sm text-red-600">{resetError}</p>
                    )}
                    <div className="flex gap-3">
                      <Button
                        variant="secondary"
                        className="flex-1"
                        onClick={handleCloseResetAuth}
                        disabled={resetLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={handleConfirmReset}
                        disabled={resetLoading}
                      >
                        {resetLoading ? 'Resetting...' : 'Reset Authenticator'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Setup Step - Show QR Code */}
                {resetStep === 'setup' && (
                  <div className="flex flex-col items-center">
                    <div className="mb-4 rounded-full bg-blue-100 p-3">
                      <ShieldCheckIcon className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="mb-2 text-base font-medium text-gray-900">Scan QR Code</h3>
                    <p className="mb-6 text-center text-sm text-gray-500">
                      Scan this QR code with Microsoft Authenticator, Google Authenticator, or any TOTP app.
                    </p>

                    {/* QR Code */}
                    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
                      <img src={resetQrCode} alt="QR Code for authenticator app" className="h-48 w-48" />
                    </div>

                    {/* Manual Entry Toggle */}
                    <button
                      onClick={() => setShowManualEntry(!showManualEntry)}
                      className="mb-4 text-sm text-primary-600 hover:text-primary-700"
                    >
                      {showManualEntry ? 'Hide manual entry key' : "Can't scan? Enter key manually"}
                    </button>

                    {showManualEntry && (
                      <div className="mb-4 w-full rounded-lg bg-gray-50 p-3">
                        <p className="mb-1 text-xs font-medium text-gray-500">Manual Entry Key:</p>
                        <p className="font-mono text-sm text-gray-900 select-all">{formatSecret(resetSecret)}</p>
                      </div>
                    )}

                    {/* App Download Links */}
                    <div className="mb-4 w-full">
                      <p className="text-xs text-gray-500 text-center mb-3">
                        Don't have an authenticator app? Download one:
                      </p>
                      <div className="flex justify-center gap-6">
                        {/* Microsoft Authenticator */}
                        <a
                          href="https://www.microsoft.com/en-us/security/mobile-authenticator-app"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-col items-center gap-1 text-center hover:opacity-80 transition-opacity"
                        >
                          <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center">
                            <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>
                            </svg>
                          </div>
                          <span className="text-xs text-gray-600">Microsoft</span>
                        </a>
                        {/* Google Authenticator */}
                        <a
                          href="https://support.google.com/accounts/answer/1066447"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-col items-center gap-1 text-center hover:opacity-80 transition-opacity"
                        >
                          <div className="w-12 h-12 rounded-lg bg-gray-800 flex items-center justify-center">
                            <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm0 4.8a7.2 7.2 0 110 14.4 7.2 7.2 0 010-14.4zm0 2.4a4.8 4.8 0 100 9.6 4.8 4.8 0 000-9.6z"/>
                            </svg>
                          </div>
                          <span className="text-xs text-gray-600">Google</span>
                        </a>
                      </div>
                    </div>

                    <div className="mb-4 w-full border-t border-gray-200" />

                    <p className="mb-3 text-sm text-gray-600">
                      Enter the 6-digit code from your app:
                    </p>

                    <CodeInput
                      value={resetCode}
                      onChange={setResetCode}
                      onComplete={handleVerifyResetCode}
                      error={!!resetError}
                      disabled={resetLoading}
                    />

                    {resetError && <p className="mt-3 text-sm text-red-600">{resetError}</p>}
                    {resetLoading && <p className="mt-3 text-sm text-gray-500">Verifying...</p>}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex justify-end">
                  <button
                    onClick={handleCloseResetAuth}
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Basic Info Modal */}
      {editType === 'basic' && editingProfileId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCancelEdit} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl">
              {/* Header */}
              <div className="border-b border-gray-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Edit Profile Info</h2>
                  <button
                    onClick={handleCancelEdit}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-4">
                {(() => {
                  const profile = profiles.find(p => p.id === editingProfileId);
                  const isIndividual = profile?.type === 'INDIVIDUAL';
                  return (
                    <>
                      {isIndividual ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
                          <input
                            type="text"
                            value={basicFormData.legalName}
                            onChange={(e) => setBasicFormData(prev => ({ ...prev, legalName: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      ) : (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Entity Name</label>
                            <input
                              type="text"
                              value={basicFormData.entityName}
                              onChange={(e) => setBasicFormData(prev => ({ ...prev, entityName: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Signer</label>
                            <input
                              type="text"
                              value={basicFormData.authorizedSigner}
                              onChange={(e) => setBasicFormData(prev => ({ ...prev, authorizedSigner: e.target.value }))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                          </div>
                        </>
                      )}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mailing Address</label>
                        <input
                          type="text"
                          value={basicFormData.address}
                          onChange={(e) => setBasicFormData(prev => ({ ...prev, address: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                          <input
                            type="text"
                            value={basicFormData.city}
                            onChange={(e) => setBasicFormData(prev => ({ ...prev, city: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                          <input
                            type="text"
                            value={basicFormData.state}
                            onChange={(e) => setBasicFormData(prev => ({ ...prev, state: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            maxLength={2}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                          <input
                            type="text"
                            value={basicFormData.zipCode}
                            onChange={(e) => setBasicFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            maxLength={10}
                          />
                        </div>
                      </div>
                    </>
                  );
                })()}

                {editError && <p className="text-sm text-red-600">{editError}</p>}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={handleCancelEdit} disabled={editSaving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveBasic} disabled={editSaving}>
                    {editSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tax ID Modal */}
      {editType === 'taxId' && editingProfileId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCancelEdit} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl">
              {/* Header */}
              <div className="border-b border-gray-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Edit {profiles.find(p => p.id === editingProfileId)?.type === 'INDIVIDUAL' ? 'SSN' : 'FEIN'}
                  </h2>
                  <button
                    onClick={handleCancelEdit}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-4">
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <ShieldCheckIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    This information is encrypted and stored securely.
                  </p>
                </div>

                {(() => {
                  const profile = profiles.find(p => p.id === editingProfileId);
                  const isIndividual = profile?.type === 'INDIVIDUAL';
                  return isIndividual ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Social Security Number (SSN)
                      </label>
                      <input
                        type="text"
                        value={taxIdFormData.ssn}
                        onChange={(e) => {
                          // Format as user types: XXX-XX-XXXX
                          const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                          let formatted = value;
                          if (value.length > 3) formatted = value.slice(0, 3) + '-' + value.slice(3);
                          if (value.length > 5) formatted = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5);
                          setTaxIdFormData(prev => ({ ...prev, ssn: formatted }));
                        }}
                        placeholder="XXX-XX-XXXX"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Current: {profile?.ssnMasked || 'Not set'}
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Federal Employer ID Number (FEIN)
                      </label>
                      <input
                        type="text"
                        value={taxIdFormData.fein}
                        onChange={(e) => {
                          // Format as user types: XX-XXXXXXX
                          const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                          let formatted = value;
                          if (value.length > 2) formatted = value.slice(0, 2) + '-' + value.slice(2);
                          setTaxIdFormData(prev => ({ ...prev, fein: formatted }));
                        }}
                        placeholder="XX-XXXXXXX"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Current: {profile?.feinMasked || 'Not set'}
                      </p>
                    </div>
                  );
                })()}

                {editError && <p className="text-sm text-red-600">{editError}</p>}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={handleCancelEdit} disabled={editSaving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveSensitive} disabled={editSaving}>
                    {editSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Banking Modal */}
      {editType === 'banking' && editingProfileId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleCancelEdit} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform overflow-hidden rounded-xl bg-white shadow-2xl">
              {/* Header */}
              <div className="border-b border-gray-100 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">Edit Banking Information</h2>
                  <button
                    onClick={handleCancelEdit}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6 space-y-4">
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <ShieldCheckIcon className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    Banking information is encrypted and stored securely.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={bankingFormData.bankName}
                    onChange={(e) => setBankingFormData(prev => ({ ...prev, bankName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                  <select
                    value={bankingFormData.bankAccountType}
                    onChange={(e) => setBankingFormData(prev => ({ ...prev, bankAccountType: e.target.value as 'CHECKING' | 'SAVINGS' | '' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select account type</option>
                    <option value="CHECKING">Checking</option>
                    <option value="SAVINGS">Savings</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Routing Number</label>
                  <input
                    type="text"
                    value={bankingFormData.routingNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                      setBankingFormData(prev => ({ ...prev, routingNumber: value }));
                    }}
                    placeholder="9 digits"
                    maxLength={9}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                  />
                  {(() => {
                    const profile = profiles.find(p => p.id === editingProfileId);
                    return profile?.routingNumberMasked && (
                      <p className="mt-1 text-xs text-gray-500">Current: {profile.routingNumberMasked}</p>
                    );
                  })()}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input
                    type="text"
                    value={bankingFormData.accountNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 17);
                      setBankingFormData(prev => ({ ...prev, accountNumber: value }));
                    }}
                    placeholder="Up to 17 digits"
                    maxLength={17}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono"
                  />
                  {(() => {
                    const profile = profiles.find(p => p.id === editingProfileId);
                    return profile?.accountNumberMasked && (
                      <p className="mt-1 text-xs text-gray-500">Current: {profile.accountNumberMasked}</p>
                    );
                  })()}
                </div>

                {editError && <p className="text-sm text-red-600">{editError}</p>}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-100 bg-gray-50 px-6 py-4">
                <div className="flex justify-end gap-3">
                  <Button variant="secondary" onClick={handleCancelEdit} disabled={editSaving}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveSensitive} disabled={editSaving}>
                    {editSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Info Tab */}
      {activeTab === 'profile' && (
        <div className="space-y-6">
          {/* Account Info */}
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-3">
                <UserCircleIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                </div>
                <button
                  onClick={() => alert('Cannot edit email. Please contact or message admin to approve an email change.')}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                  title="Edit email"
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              </div>
              {/* Phone number hidden - will be used for SMS 2FA in future */}
            </div>
          </Card>

          {/* Investor Profiles */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Investor Profiles</h2>
            <div className="space-y-4">
              {profiles.map((profile) => (
                <Card key={profile.id} padding="md">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary-100 rounded-lg">
                        {profile.type === 'INDIVIDUAL' ? (
                          <UserCircleIcon className="h-6 w-6 text-primary-600" />
                        ) : (
                          <BuildingOfficeIcon className="h-6 w-6 text-primary-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-semibold text-gray-900">
                            {profile.entityName || profile.legalName}
                          </h3>
                          <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                            {getProfileTypeLabel(profile.type)}
                          </span>
                        </div>
                        {profile.authorizedSigner && (
                          <p className="text-sm text-gray-500 mt-1">
                            Signer: {profile.authorizedSigner}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          {profile.address}, {profile.city}, {profile.state} {profile.zipCode}
                        </p>
                      </div>
                    </div>
                    {/* Edit Basic Info Button */}
                    <button
                      onClick={() => handleStartBasicEdit(profile)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      title="Edit name and address"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Tax ID and Banking Info */}
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
                    {/* Tax ID */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-gray-500">
                          {profile.type === 'INDIVIDUAL' ? 'SSN' : 'FEIN'}
                        </p>
                        <button
                          onClick={() => handleStartSensitiveEdit(profile, 'taxId')}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title={`Edit ${profile.type === 'INDIVIDUAL' ? 'SSN' : 'FEIN'}`}
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <PressToRevealButton
                        profileId={profile.id}
                        field={profile.type === 'INDIVIDUAL' ? 'ssn' : 'fein'}
                        maskedValue={profile.type === 'INDIVIDUAL' ? (profile.ssnMasked || '***-**-****') : (profile.feinMasked || '**-*******')}
                        hasValidSession={hasValidTotpSession()}
                        onRequestReveal={handleRequestReveal}
                        formatValue={profile.type === 'INDIVIDUAL' ? formatSSN : formatFEIN}
                      />
                    </div>

                    {/* Banking Information */}
                    <div className="pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-medium text-gray-700">Banking Information</h4>
                        <button
                          onClick={() => handleStartSensitiveEdit(profile, 'banking')}
                          className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                          title="Edit banking information"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Bank Name */}
                        <div>
                          <p className="text-xs text-gray-500">Bank Name</p>
                          <p className="text-sm font-medium text-gray-900">
                            {profile.bankName || 'Not set'}
                          </p>
                        </div>

                        {/* Account Type */}
                        <div>
                          <p className="text-xs text-gray-500">Account Type</p>
                          <p className="text-sm font-medium text-gray-900">
                            {profile.bankAccountType || 'Not set'}
                          </p>
                        </div>

                        {/* Routing Number */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Routing Number</p>
                          {profile.routingNumberMasked ? (
                            <PressToRevealButton
                              profileId={profile.id}
                              field="routing"
                              maskedValue={profile.routingNumberMasked}
                              hasValidSession={hasValidTotpSession()}
                              onRequestReveal={handleRequestReveal}
                            />
                          ) : (
                            <p className="text-sm font-medium text-gray-500">Not set</p>
                          )}
                        </div>

                        {/* Account Number */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Account Number</p>
                          {profile.accountNumberMasked ? (
                            <PressToRevealButton
                              profileId={profile.id}
                              field="account"
                              maskedValue={profile.accountNumberMasked}
                              hasValidSession={hasValidTotpSession()}
                              onRequestReveal={handleRequestReveal}
                            />
                          ) : (
                            <p className="text-sm font-medium text-gray-500">Not set</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Security Settings</h2>

            <div className="space-y-6">
              {/* Authenticator 2FA Status */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100">
                    <ShieldCheckIcon className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      Two-Factor Authentication (Authenticator App)
                    </p>
                    <p className="text-xs text-gray-500">
                      Use Microsoft Authenticator or Google Authenticator to verify sensitive operations
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                    Required
                  </span>
                  <button
                    onClick={handleOpenResetAuth}
                    className="text-xs text-primary-600 hover:text-primary-800 underline"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Phone Number - hidden for now, will be used for SMS 2FA in future */}

              {/* Recent Activity */}
              <div>
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Recent Security Activity
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-2 text-sm">
                    <span className="text-gray-600">Last login</span>
                    <span className="text-gray-900">
                      {new Date().toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 text-sm">
                    <span className="text-gray-600">Password last changed</span>
                    <span className="text-gray-900">Never</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Sensitive Data Access
            </h2>
            <p className="text-sm text-gray-500">
              For your security, viewing sensitive information (SSN, banking details)
              requires verification through your authenticator app. Once verified, you can press and hold the "Hold to View"
              button to reveal data - it will only be visible while you're holding the button. Your verification session
              is valid for 5 minutes, or until you leave the page or switch tabs.
            </p>
          </Card>
        </div>
      )}

    </div>
  );
}

export default ProfilePage;
